import { useSelector } from "react-redux";
import { Box, Grid, Paper, Typography, Chip, Button, Skeleton } from "@mui/material";
import { Article, PostAdd, Category, Language, OpenInNew } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useGetWebsiteManageSettingsQuery, useGetCmsPagesQuery, useGetCmsPostsQuery } from "../../../app/websiteApiSlice";
import { selectCurrentUser } from "../auth/authSlice";

function StatCard({ icon, label, value, to, loading }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
        {icon}
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
      {loading ? (
        <Skeleton width={40} height={36} />
      ) : (
        <Typography variant="h4" fontWeight={700}>{value}</Typography>
      )}
      <Button size="small" component={Link} to={to} sx={{ mt: 1, p: 0, textTransform: "none" }}>
        Manage →
      </Button>
    </Paper>
  );
}

export default function CmsDashboard() {
  const user = useSelector(selectCurrentUser);
  const { data: settings, isLoading: loadingSettings } = useGetWebsiteManageSettingsQuery();
  const { data: pages, isLoading: loadingPages } = useGetCmsPagesQuery();
  const { data: posts, isLoading: loadingPosts } = useGetCmsPostsQuery();

  const publishedPages = pages?.filter((p) => p.status === "published").length ?? 0;
  const draftPages = pages?.filter((p) => p.status === "draft").length ?? 0;
  const publishedPosts = posts?.filter((p) => p.status === "published").length ?? 0;
  const draftPosts = posts?.filter((p) => p.status === "draft").length ?? 0;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>CMS Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {user?.username}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {loadingSettings ? (
            <Skeleton width={80} height={32} />
          ) : (
            <Chip
              icon={<Language fontSize="small" />}
              label={settings?.enabled ? "Website Live" : "Website Off"}
              color={settings?.enabled ? "success" : "default"}
              size="small"
            />
          )}
          {settings?.enabled && (
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNew fontSize="small" />}
              component="a"
              href="/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Site
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Article color="primary" />}
            label="Published Pages"
            value={publishedPages}
            to="/cms/pages"
            loading={loadingPages}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Article color="disabled" />}
            label="Draft Pages"
            value={draftPages}
            to="/cms/pages"
            loading={loadingPages}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PostAdd color="secondary" />}
            label="Published Posts"
            value={publishedPosts}
            to="/cms/posts"
            loading={loadingPosts}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PostAdd color="disabled" />}
            label="Draft Posts"
            value={draftPosts}
            to="/cms/posts"
            loading={loadingPosts}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Quick Actions</Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Button variant="contained" component={Link} to="/cms/pages/new" startIcon={<Article />}>
                New Page
              </Button>
              <Button variant="outlined" component={Link} to="/cms/posts/new" startIcon={<PostAdd />}>
                New Post
              </Button>
              <Button variant="text" component={Link} to="/cms/settings" startIcon={<Category />}>
                Website Settings
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Active Theme</Typography>
            {loadingSettings ? (
              <Skeleton height={40} />
            ) : (
              <Box>
                <Chip
                  label={settings?.theme ? settings.theme.charAt(0).toUpperCase() + settings.theme.slice(1) : "Modern"}
                  color="primary"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {settings?.tagline || "No tagline set"}
                </Typography>
                <Button size="small" component={Link} to="/cms/settings" sx={{ mt: 1, p: 0, textTransform: "none" }}>
                  Change theme →
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
